import Button from '@ui/Button'
import Field from '@ui/Field'
import Input from '@ui/Input'
import type { MultiFactorResolver } from 'firebase/auth'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  createPasswordAccount,
  IdpError,
  type IdpErrorCode,
  sendPasswordReset,
  type SignInMethod,
  signInWithPassword,
  startSession,
} from '@/auth/idp'

type View = { kind: 'sign-in' } | { kind: 'create' } | { kind: 'reset' }

const errorCode = (err: unknown): IdpErrorCode =>
  err instanceof IdpError ? err.code : 'unknown'

export type CheckEmailReason = 'verify' | 'reset' | 'reset-recently-sent'

export type TotpStep = {
  resolver: MultiFactorResolver
  method: SignInMethod
  email?: string
}

type Props = {
  /** The login page swaps its whole card for the check-email state. */
  onCheckEmail: (reason: CheckEmailReason, email: string) => void
  /** The login page swaps its whole card for the code step. */
  onTotpRequired: (step: TotpStep) => void
  /** Refills the form when the user comes back from the code step. */
  initialEmail?: string
}

const PasswordLogin = ({
  onCheckEmail,
  onTotpRequired,
  initialEmail = '',
}: Props) => {
  const { t } = useTranslation()
  const [view, setView] = useState<View>({ kind: 'sign-in' })
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)

  const go = (next: View) => {
    setError(null)
    setView(next)
  }

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      setError(errorCode(err))
    } finally {
      setBusy(false)
    }
  }

  const finish = async (user: Parameters<typeof startSession>[0]) => {
    const redirect = await startSession(user)
    window.location.assign(redirect)
  }

  const onSignIn = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      const result = await signInWithPassword(email.trim(), password)
      if (result.kind === 'totp-required') {
        onTotpRequired(result)
        return
      }
      await finish(result.user)
    })
  }

  const onCreate = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      await createPasswordAccount(email.trim(), password)
      onCheckEmail('verify', email.trim())
    })
  }

  const onReset = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      const status = await sendPasswordReset(email.trim())
      onCheckEmail(
        status === 'recently-sent' ? 'reset-recently-sent' : 'reset',
        email.trim()
      )
    })
  }

  const errorLine = error && (
    <p role="alert" className="text-sm text-flag">
      {t(`auth.password.errors.${error}` as const)}
    </p>
  )

  const backToSignIn = (
    <Button variant="ghost" fullWidth onClick={() => go({ kind: 'sign-in' })}>
      {t('auth.password.back-to-sign-in')}
    </Button>
  )

  const emailField = (
    <Field label={t('auth.password.email')}>
      <Input
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
    </Field>
  )

  if (view.kind === 'reset') {
    return (
      <form key="reset" onSubmit={onReset} className="flex flex-col gap-4">
        <p className="text-sm text-ink-700">{t('auth.password.reset-intro')}</p>
        {emailField}
        {errorLine}
        <Button type="submit" fullWidth disabled={busy}>
          {busy
            ? t('auth.password.sending-link')
            : t('auth.password.send-reset')}
        </Button>
        {backToSignIn}
      </form>
    )
  }

  const creating = view.kind === 'create'
  let submitLabel = t('auth.password.sign-in')
  if (creating) {
    submitLabel = busy ? t('auth.password.creating') : t('auth.password.create')
  } else if (busy) submitLabel = t('auth.logging-in')

  return (
    <form
      key={view.kind}
      onSubmit={creating ? onCreate : onSignIn}
      className="flex flex-col gap-4"
    >
      {creating && (
        <p className="text-sm text-ink-700">
          {t('auth.password.create-intro')}
        </p>
      )}
      {emailField}
      <Field
        label={t('auth.password.password')}
        hint={creating ? t('auth.password.password-hint') : undefined}
      >
        <Input
          type="password"
          autoComplete={creating ? 'new-password' : 'current-password'}
          minLength={creating ? 8 : undefined}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </Field>
      {errorLine}
      <Button type="submit" variant="secondary" fullWidth disabled={busy}>
        {submitLabel}
      </Button>
      {creating ? (
        backToSignIn
      ) : (
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            fullWidth
            onClick={() => go({ kind: 'reset' })}
          >
            {t('auth.password.forgot')}
          </Button>
          <Button
            variant="ghost"
            fullWidth
            onClick={() => go({ kind: 'create' })}
          >
            {t('auth.password.first-time')}
          </Button>
        </div>
      )}
    </form>
  )
}

export default PasswordLogin
